import { useState, useEffect } from 'react';
import { users, admin, deletionRequests } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, UserPlus, Shield, ShieldOff, Check, X, Trash2, RefreshCw,
  AlertTriangle, Clock, UserCog, Mail, Key
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

function CreateUserDialog({ open, onOpenChange, onSubmit }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'member',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ email: '', password: '', name: '', role: 'member' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Crear Usuario
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre completo"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="usuario@transtar.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Contraseña *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label>Rol</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>Miembro</span>
                  </div>
                </SelectItem>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span>Administrador</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              <UserPlus className="h-4 w-4 mr-2" />
              Crear Usuario
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeletionRequestCard({ request, onApprove, onReject }) {
  const typeLabels = {
    task: 'Tarea',
    client: 'Cliente',
    contact: 'Contacto',
    opportunity: 'Oportunidad',
    invoice: 'Factura',
    quote: 'Cotización',
    expense: 'Gasto',
  };

  return (
    <div className="p-4 rounded-lg border hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline">{typeLabels[request.item_type] || request.item_type}</Badge>
            <span className="font-medium">{request.item_name}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Solicitado por: <span className="font-medium">{request.requested_by_name}</span>
          </p>
          {request.reason && (
            <p className="text-sm text-muted-foreground mt-1">
              Razón: {request.reason}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {formatDateTime(request.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReject(request.request_id)} className="text-rose-600">
            <X className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={() => onApprove(request.request_id)} className="bg-emerald-600 hover:bg-emerald-700">
            <Check className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [usersList, setUsersList] = useState([]);
  const [deletionRequestsList, setDeletionRequestsList] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [createUserOpen, setCreateUserOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [usersRes, requestsRes, countRes] = await Promise.all([
        users.getAll(),
        admin.getDeletionRequests('pending'),
        admin.getPendingDeletionsCount()
      ]);
      setUsersList(usersRes.data || []);
      setDeletionRequestsList(requestsRes.data || []);
      setPendingCount(countRes.data?.pending_count || 0);
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (data) => {
    try {
      await admin.createUser(data);
      toast.success('Usuario creado exitosamente');
      setCreateUserOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      if (currentStatus === 'active') {
        await admin.deactivateUser(userId);
        toast.success('Usuario desactivado');
      } else {
        await admin.reactivateUser(userId);
        toast.success('Usuario reactivado');
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await admin.updateUser(userId, { role: newRole });
      toast.success('Rol actualizado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  const handleApproveDeletion = async (requestId) => {
    try {
      await admin.approveDeletion(requestId);
      toast.success('Eliminación aprobada');
      loadData();
    } catch (err) {
      toast.error('Error al aprobar eliminación');
    }
  };

  const handleRejectDeletion = async (requestId) => {
    try {
      await admin.rejectDeletion(requestId);
      toast.success('Solicitud rechazada');
      loadData();
    } catch (err) {
      toast.error('Error al rechazar solicitud');
    }
  };

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const activeUsers = usersList.filter(u => u.status !== 'inactive');
  const inactiveUsers = usersList.filter(u => u.status === 'inactive');

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Administración</h1>
          <p className="text-muted-foreground">Gestión de usuarios y permisos</p>
        </div>
        <Button onClick={() => setCreateUserOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Crear Usuario
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Usuarios Activos</p>
                <p className="text-2xl font-bold">{activeUsers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <ShieldOff className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Usuarios Inactivos</p>
                <p className="text-2xl font-bold">{inactiveUsers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={pendingCount > 0 ? 'border-amber-300 bg-amber-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${pendingCount > 0 ? 'bg-amber-200' : 'bg-slate-100'}`}>
                <Trash2 className={`h-6 w-6 ${pendingCount > 0 ? 'text-amber-700' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Solicitudes Pendientes</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="deletions" className="gap-2">
            <Trash2 className="h-4 w-4" />
            Solicitudes de Eliminación
            {pendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Usuarios del Sistema</CardTitle>
              <CardDescription>
                Gestiona los accesos y roles de los usuarios
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Creado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersList.map((u) => (
                    <TableRow key={u.user_id} className={u.status === 'inactive' ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                            u.role === 'admin' ? 'bg-amber-500' : 'bg-slate-500'
                          }`}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleChangeRole(u.user_id, value)}
                          disabled={u.user_id === user.user_id}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Miembro</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.status === 'active' ? 'success' : 'secondary'}>
                          {u.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(u.created_at)}
                      </TableCell>
                      <TableCell>
                        {u.user_id !== user.user_id && (
                          <Button
                            size="sm"
                            variant={u.status === 'active' ? 'outline' : 'default'}
                            onClick={() => handleToggleUserStatus(u.user_id, u.status)}
                            className={u.status === 'active' ? 'text-rose-600' : ''}
                          >
                            {u.status === 'active' ? (
                              <>
                                <ShieldOff className="h-4 w-4 mr-1" />
                                Desactivar
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Reactivar
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deletions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Solicitudes de Eliminación Pendientes
              </CardTitle>
              <CardDescription>
                Los usuarios no pueden eliminar información sin tu aprobación
              </CardDescription>
            </CardHeader>
            <CardContent>
              {deletionRequestsList.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Check className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>No hay solicitudes pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deletionRequestsList.map((request) => (
                    <DeletionRequestCard
                      key={request.request_id}
                      request={request}
                      onApprove={handleApproveDeletion}
                      onReject={handleRejectDeletion}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateUserDialog
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
        onSubmit={handleCreateUser}
      />
    </div>
  );
}
